import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import Moment from "react-moment";
import { DispatchContext, StateContext } from "../../../store";
import { deaccessionApi } from "../../../services/DeaccessionServices";
import { truncate } from "../../../common/Functions/Truncate/Truncate";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import AutoModeIcon from "@mui/icons-material/AutoMode";

const asText = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") return value?.name ?? value?.code ?? "-";
  return "-";
};

export default function DeaccessionTable({ querySearch, museumId }) {
  const dispatch = useContext(DispatchContext);
  const { role } = useContext(StateContext);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0 });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deaccessions, setDeaccessions] = useState([]);
  const isAdminOrChief = role?.id === 1 || role?.id === 10;

  useEffect(() => {
    getDeaccession();
  }, [querySearch, page, rowsPerPage, museumId]);

  const getDeaccession = () => {
    deaccessionApi("get", null, { search: querySearch, page: page, per_page: rowsPerPage, museum_id: museumId }).then(
      (res) => {
        setDeaccessions(res?.data);
        setMeta(res?.meta);
      }
    );
  };

  const handleOpen = () => {
    navigate("/management/artifact-managements/deaccession-create");
  };

  const deaccessionDetail = (id) => {
    navigate(`/management/deaccessions/${id}`);
  };

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "IS_NOT_LOADING", payload: false });
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <div>
      <Box>
        {isAdminOrChief && (
          <Typography sx={{ mt: 5, mb: 2 }} className={"museum-font"}>
            Deaccession
          </Typography>
        )}
        <Box>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Box>
                <Box className={"flex-cx-center"}>
                  <AutoModeIcon sx={{ color: "#ff9653" }} />
                  <Typography sx={{ ml: 1, fontWeight: 600, color: "#757575", fontSize: 20 }}>
                    {meta?.total || 0}
                  </Typography>
                </Box>
                <Typography>Total Deaccession</Typography>
              </Box>
            </Grid>
            <Grid item sm={6} sx={{ textAlign: "right" }}>
              {role?.id === 2 && (
                <Button
                  onClick={handleOpen}
                  className={"text-capitalize border-radius-100"}
                  variant={"contained"}
                  color={"primary"}
                >
                  + Deaccession Request
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box className={"header-divider"} />
        <div>
          <TableContainer className={"box-shadow"} component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="caption table">
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ color: "#B3B3B3", width: 20 }}>
                    Sl.No
                  </TableCell>
                  <TableCell align="left" sx={{ color: "#B3B3B3" }}>
                    Submission Date
                  </TableCell>
                  <TableCell align="left" sx={{ color: "#B3B3B3" }}>
                    Accession No.
                  </TableCell>
                  <TableCell align="left" sx={{ color: "#B3B3B3" }}>
                    Created by
                  </TableCell>
                  <TableCell align="left" sx={{ color: "#B3B3B3" }}>
                    Museum
                  </TableCell>
                  <TableCell align="left" sx={{ color: "#B3B3B3" }}>
                    Status
                  </TableCell>
                  <TableCell align="left" sx={{ color: "#B3B3B3" }}>
                    Justification
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deaccessions?.length > 0 &&
                  deaccessions?.map((deaccession, index) => (
                    <TableRow hover key={index}>
                      <TableCell
                        align="left"
                        sx={{ color: "#3D3D3D", width: 20 }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ color: "#3D3D3D" }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        <Moment format={"ll"}>{deaccession?.submission_date}</Moment>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ color: "#3D3D3D" }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        {asText(deaccession?.accession_no)}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ color: "#3D3D3D" }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        {deaccession?.user_name}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ color: "#3D3D3D" }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        {deaccession?.museum_name}
                      </TableCell>
                      <TableCell
                        className={"text-capitalize"}
                        align="left"
                        sx={{ color: "#3D3D3D" }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        {deaccession?.state}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ color: "#3D3D3D" }}
                        onClick={() => deaccessionDetail(deaccession?.id)}
                      >
                        {truncate(deaccession?.justification, 30)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 100]}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            component="div"
            count={meta?.total}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </Box>
    </div>
  );
}
